import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom"; // remove if you're not using react-router

const About = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />

      <main className="pt-24 bg-gradient-to-r from-primary/5 via-accent/5 to-secondary/5" role="main">
        <div className="max-w-4xl mx-auto px-4 py-16">
          {/* Hero */}
          <header
            className="text-center mb-10 relative overflow-hidden rounded-3xl"
            aria-labelledby="about-title"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-accent/5 to-secondary/5" />
            <div className="relative py-12 px-6 sm:px-10">
              <h1
                id="about-title"
                className="text-4xl sm:text-5xl font-display font-bold mb-6"
              >
                About{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Flexaura
                </span>
              </h1>

              <p className="text-xl text-primary font-medium mb-6 bg-gradient-to-r from-primary/10 to-accent/10 py-4 px-6 rounded-xl border border-primary/20 inline-block">
                Life&apos;s biggest milestones, made affordable.
              </p>

              <section
                className="mt-6 bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-2xl border border-primary/15 text-left"
                aria-label="Flexaura summary"
              >
                <p className="text-lg leading-relaxed">
                  Flexaura is the GCC&apos;s first Buy Now, Pay Later marketplace
                  designed for families. From tuition fees and healthcare to
                  once-in-a-lifetime travel, we make big-ticket moments
                  accessible through trusted BNPL providers, banks, and finance
                  houses.
                </p>
              </section>
            </div>
          </header>

          {/* Content */}
          <div className="prose prose-lg max-w-none space-y-10 prose-headings:font-display prose-headings:text-primary">
            {/* Who we serve */}
            <section className="text-center bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10" aria-labelledby="serve-heading">
              <h2 id="serve-heading" className="text-2xl font-semibold mb-3">
                Who we serve
              </h2>
              <p className="text-lg leading-relaxed max-w-2xl mx-auto">
                Parents covering school fees, young couples planning their first
                holiday, or patients managing medical bills — Flexaura helps you
                spread essential costs without the stress of lump-sum payments.
              </p>
            </section>

            {/* Why we exist */}
            <section
              className="text-center bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10"
              aria-labelledby="exist-heading"
            >
              <h2 id="exist-heading" className="text-2xl font-semibold mb-3">
                Why we exist
              </h2>
              <p className="text-lg leading-relaxed max-w-2xl mx-auto">
                Big expenses can create stress, delays, or missed opportunities.
                Flexaura gives families the power to plan with confidence —
                splitting costs into manageable payments, always with
                transparency and choice.
              </p>
            </section>

            {/* How it works */}
            <section className="text-center" aria-labelledby="how-heading">
              <h2 id="how-heading" className="text-2xl font-semibold mb-6">
                How it works
              </h2>

              {/* Accessible ordered list styled as cards */}
              <ol className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto list-none counter-reset-[steps]">
                <li className="text-center p-6 rounded-lg bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
                  <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    1
                  </div>
                  <p className="font-medium text-primary">
                    Browse trusted financing options.
                  </p>
                </li>
                <li className="text-center p-6 rounded-lg bg-gradient-to-br from-accent/5 to-accent/10 border border-accent/20">
                  <div className="w-12 h-12 bg-accent text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    2
                  </div>
                  <p className="font-medium text-accent">
                    Choose the plan that suits your needs.
                  </p>
                </li>
                <li className="text-center p-6 rounded-lg bg-success/5 border border-success/20">
                  <div className="w-12 h-12 bg-success text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    3
                  </div>
                  <p className="font-medium text-success">
                    Pay over time, stress-free.
                  </p>
                </li>
              </ol>

              {/* Micro-trust helpers (optional, remove if you can't claim these yet) */}
              <div className="mt-4 text-sm text-muted-foreground">
                <p>
                  Clear terms. Multiple providers. One simple application
                  experience.
                </p>
              </div>
            </section>

            {/* Differentiator */}
            <section className="text-center bg-gradient-to-br from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10" aria-labelledby="diff-heading">
              <h2 id="diff-heading" className="text-2xl font-semibold mb-3">
                Why Flexaura is different
              </h2>
              <p className="text-lg leading-relaxed max-w-2xl mx-auto">
                Unlike single BNPL providers, Flexaura brings together multiple
                finance partners in one marketplace — giving families more
                options, better rates, and greater flexibility.
              </p>
            </section>

            {/* Values */}
            <section className="text-center" aria-labelledby="values-heading">
              <h2 id="values-heading" className="text-2xl font-semibold mb-6">
                Our values
              </h2>
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="p-6 rounded-xl border border-border bg-card">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-2">
                    Transparency
                  </h3>
                  <p className="text-muted-foreground">
                    Clear terms, no hidden surprises.
                  </p>
                </div>

                <div className="p-6 rounded-xl border border-border bg-card">
                  <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-accent mb-2">
                    Accessibility
                  </h3>
                  <p className="text-muted-foreground">
                    Finance options for every family.
                  </p>
                </div>

                <div className="p-6 rounded-xl border border-border bg-card">
                  <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-primary mb-2">
                    Trust
                  </h3>
                  <p className="text-muted-foreground">
                    Built on compliance, partnerships, and care.
                  </p>
                </div>
              </div>
            </section>

            {/* Origin + CTA */}
            <section
              className="text-center bg-gradient-to-r from-primary/5 to-accent/5 p-8 rounded-xl border border-primary/10"
              aria-label="Origin and call to action"
            >
              <p className="text-lg font-medium text-primary mb-3">
                Founded in Dubai, Flexaura is reshaping how the region approaches
                family finance.
              </p>
              <p className="text-lg font-semibold">
                Discover how Flexaura can help with your next milestone.
              </p>

              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                {/* Replace Link with <a href="..."> if not using react-router */}
                <Link
                  to="/marketplace"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 bg-primary text-primary-foreground font-medium hover:opacity-90 transition"
                  aria-label="Explore the Flexaura marketplace"
                >
                  Explore marketplace
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center rounded-xl px-5 py-3 border border-border bg-card hover:bg-accent/10 transition"
                  aria-label="Contact Flexaura"
                >
                  Talk to us
                </Link>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default About;